import {useCountry, useDepositAccount} from '@resurs-internal/rb-app-api';
import type {DepositAccount} from '@resurs-internal/rb-app-api/api/customer-frontend-service';
import {useTranslate} from 'hooks';
import {useNavigate} from '@tanstack/react-router';
import {createBankIDUrl} from '../../../../services/createBankIDUrl';
import {createRedirectPath} from '../../../../services/uri/createRedirectPath';
import {snackbar} from '../../../../services/snackbar';
import type {SendMoneyFormValues} from '../SavingsAccountWithdrawalDialog';

const REDIRECT_PATH = '/withdrawal/callback';

type UseWithdrawalSubmitParams = {
  accountId: string;
  toAccount: DepositAccount['disbursementAccount'];
  onClose: () => void;
};

export const useWithdrawalSubmit = ({
  accountId,
  toAccount,
  onClose,
}: UseWithdrawalSubmitParams) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const country = useCountry();
  const account = useDepositAccount(accountId);
  const fromAccount = account.query.data;

  const submit = async (values: SendMoneyFormValues) => {
    if (!fromAccount) {
      return snackbar.warning(translate('error.withdrawal.from.invalid'));
    }
    if (!toAccount) {
      return snackbar.warning(translate('error.withdrawal.to.invalid'));
    }

    const isSEBankID = country.code === 'SE';
    const successRedirect = createRedirectPath(REDIRECT_PATH, {
      accountId,
      status: 'SUCCESSFUL',
    });
    const cancelRedirect = createRedirectPath(REDIRECT_PATH, {
      accountId,
      status: 'ERROR',
    });

    const response = await account.withdrawal.mutateAsync({
      message: values.message,
      amount: {
        amount: Number(values.amount.replace(',', '.')),
        currency: fromAccount?.currentBalance?.currency,
      },
      destination: {
        accountPrefix: toAccount.accountPrefix || '',
        accountNumber: toAccount.accountNumber || '',
      },
      disbursementAccountId: fromAccount?.disbursementAccount?.id,
      recipientName: values.recipientName,
      locale: country.locale,
      onSuccessUri: isSEBankID ? undefined : successRedirect,
      onCancelledUri: isSEBankID ? undefined : cancelRedirect,
    });

    if (isSEBankID) {
      const url = createBankIDUrl({
        token: response.signatureId,
        redirect: REDIRECT_PATH,
        accountId,
      });
      onClose();
      navigate({to: url});
    } else if (response.redirect) {
      window.location.href = response.redirect;
    } else {
      navigate({to: '/error'});
    }
  };

  // The mutation state belongs to this hook instance, so the dialog has to
  // read the error from here to show why a withdrawal was rejected.
  return {submit, error: account.withdrawal.error};
};
